/**
 * SystemSettings Component
 * 
 * This component allows administrators to configure system-wide settings including:
 * - Maximum number of free certificates
 * - Email certificate settings
 * - Batch report settings for unverified students
 * - Subscription reconciliation
 * 
 * All text strings use the t() function from react-i18next with appropriate fallback text
 * to ensure proper internationalization support for all languages.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Paper,
    Grid,
    Alert,
    CircularProgress,
    IconButton,
    Divider,
    Chip,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Checkbox
} from '@mui/material';
import { 
    ToggleOn as EnableIcon,
    ToggleOff as DisableIcon,
    ExpandMore as ExpandMoreIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Sync as SyncIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { AdminService } from '../../services/modules/admin';
import { SubscriptionService } from '../../services/modules/subscription';
import { ReconciliationItem, ReconciliationResult } from '../../services/types/subscription';

const SystemSettings: React.FC = () => {
    const [maxFreeCertificates, setMaxFreeCertificates] = useState<string>('1');
    const [originalValue, setOriginalValue] = useState<string>('1');
    const [emailCertificateEnabled, setEmailCertificateEnabled] = useState<boolean>(true);
    const [originalEmailEnabled, setOriginalEmailEnabled] = useState<boolean>(true);
    const [allowNameFieldsEditing, setAllowNameFieldsEditing] = useState<boolean>(true);
    const [originalAllowNameFieldsEditing, setOriginalAllowNameFieldsEditing] = useState<boolean>(true);
    
    // Batch report settings
    const [batchReportTime, setBatchReportTime] = useState<string>('08:00');
    const [originalBatchReportTime, setOriginalBatchReportTime] = useState<string>('08:00');
    const [batchReportEmails, setBatchReportEmails] = useState<string[]>([]);
    const [originalBatchReportEmails, setOriginalBatchReportEmails] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState<string>('');

    // Format date as dd-MMM-YYYY hh:mm:ss AM/PM
    const formatDate = (date: Date): string => {
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        
        let hours = date.getHours();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} ${ampm}`;
    };
    
    // Format date with date and time on separate lines
    const formatDateTwoLines = (dateString: string | null | undefined): React.ReactNode => {
        if (!dateString) return '-';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; // Return original if invalid
            
            const day = date.getDate().toString().padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            
            let hours = date.getHours();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // the hour '0' should be '12'
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            
            return (
                <>
                    <div>{`${day}-${month}-${year}`}</div>
                    <div style={{ fontSize: '0.85em', color: 'rgba(0, 0, 0, 0.6)' }}>{`${hours}:${minutes}:${seconds} ${ampm}`}</div>
                </>
            );
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString; // Return original on error
        }
    };

    // Subscription reconciliation settings
    const [reconciliationData, setReconciliationData] = useState<ReconciliationResult | null>(null);
    const [loadingReconciliation, setLoadingReconciliation] = useState(false);
    const [reconciliationMessage, setReconciliationMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
    const [reconciliationItems, setReconciliationItems] = useState<ReconciliationItem[]>([]);
    const [reconciliationResults, setReconciliationResults] = useState<ReconciliationResult | null>(null);
    const [currentJobId, setCurrentJobId] = useState<string | null>(null);
    const [processingItems, setProcessingItems] = useState<Record<string, boolean>>({});
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    // Using a ref for the polling interval to avoid dependency issues
    const intervalIdRef = useRef<number | null>(null);
    const [reconciliationJobId, setReconciliationJobId] = useState<string | null>(null);
    const [reconciliationProgress, setReconciliationProgress] = useState<{ current: number, total: number } | null>(null);
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const { user } = useAuth();
    const { t } = useTranslation();
    
    // Cleanup polling interval when component unmounts
    useEffect(() => {
        return () => {
            if (intervalIdRef.current) {
                console.log('Component unmounting, clearing polling interval:', intervalIdRef.current);
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        };
    }, []);

    const fetchSettings = async () => {
        if (!user?.token) return;
        
        try {
            const [maxCerts, emailEnabled, nameFieldsEditing, reportTime, reportEmails] = await Promise.all([
                AdminService.getMaxFreeCertificates(user.token),
                AdminService.getEmailCertificateEnabled(user.token),
                AdminService.getAllowNameFieldsEditing(user.token),
                AdminService.getBatchReportTime(user.token),
                AdminService.getBatchReportEmails(user.token)
            ]);
            setMaxFreeCertificates(maxCerts.toString());
            setOriginalValue(maxCerts.toString());
            setEmailCertificateEnabled(emailEnabled);
            setOriginalEmailEnabled(emailEnabled);
            setAllowNameFieldsEditing(nameFieldsEditing);
            setOriginalAllowNameFieldsEditing(nameFieldsEditing);
            setBatchReportTime(reportTime || '08:00');
            setOriginalBatchReportTime(reportTime || '08:00');
            setBatchReportEmails(reportEmails || []);
            setOriginalBatchReportEmails(reportEmails || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching settings:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, [user?.token]);

    const handleSave = async () => {
        if (!user?.token) return;
        
        setSaving(true);
        setMessage(null);
        
        try {
            await Promise.all([
                AdminService.updateMaxFreeCertificates(user.token, parseInt(maxFreeCertificates)),
                AdminService.updateEmailCertificateEnabled(user.token, emailCertificateEnabled),
                AdminService.updateAllowNameFieldsEditing(user.token, allowNameFieldsEditing),
                AdminService.updateBatchReportTime(user.token, batchReportTime),
                AdminService.updateBatchReportEmails(user.token, batchReportEmails)
            ]);
            
            setOriginalValue(maxFreeCertificates);
            setOriginalEmailEnabled(emailCertificateEnabled);
            setOriginalAllowNameFieldsEditing(allowNameFieldsEditing);
            setOriginalBatchReportTime(batchReportTime);
            setOriginalBatchReportEmails([...batchReportEmails]);
            
            setMessage({
                type: 'success',
                text: t('admin.systemSettings.saveSuccess', 'Settings saved successfully!')
            });
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({
                type: 'error',
                text: t('admin.systemSettings.saveError', 'Error saving settings. Please try again.')
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAddEmail = () => {
        if (!newEmail || batchReportEmails.includes(newEmail)) {
            return;
        }
        
        setBatchReportEmails([...batchReportEmails, newEmail]);
        setNewEmail('');
        setMessage(null);
    };
    
    const handleRemoveEmail = (email: string) => {
        setBatchReportEmails(batchReportEmails.filter(e => e !== email));
        setMessage(null);
    };
    
    const handleTriggerBatchReport = async () => {
        if (!user?.token) return;
        
        setGenerating(true);
        setMessage(null);
        
        try {
            await AdminService.triggerUnverifiedStudentsBatchReport(user.token);
            setMessage({
                type: 'success',
                text: t('admin.systemSettings.batchReportSuccess', 'Batch report generated and sent successfully!')
            });
        } catch (error) {
            console.error('Error triggering batch report:', error);
            setMessage({
                type: 'error',
                text: t('admin.systemSettings.batchReportError', 'Error generating batch report. Please try again.')
            });
        } finally {
            setGenerating(false);
        }
    };

    // Clean up polling interval on component unmount
    useEffect(() => {
        return () => {
            // Clear any active intervals when component unmounts
            if (intervalIdRef.current) {
                window.clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        };
    }, []);
    
    // Subscription reconciliation functions
    const checkReconciliation = async () => {
        if (!user?.token) return;
        
        setLoadingReconciliation(true);
        setReconciliationMessage(null);
        setReconciliationData(null);
        setReconciliationProgress(null);
        setSelectedItems([]);
        
        try {
            // Start a new background job
            const jobResponse = await SubscriptionService.startReconciliationJob(user.token);
            setReconciliationJobId(jobResponse.job_id);
            
            // Show initial message
            setReconciliationMessage({
                type: 'info',
                text: t('admin.systemSettings.reconciliation.jobStarted', 
                    'Reconciliation job started. This process will run in the background and may take several minutes to complete.')
            });
            
            // Set up polling to check job status immediately and then every 5 seconds
            
            // Check status immediately first
            try {
                const initialStatus = await SubscriptionService.checkReconciliationJobStatus(user.token, jobResponse.job_id);
                
                // Update UI with initial status
                setReconciliationData(initialStatus);
                if (initialStatus.pagination) {
                    setReconciliationProgress({
                        current: initialStatus.pagination.processed_count,
                        total: initialStatus.pagination.total_count
                    });
                }
                
                // If job is already complete, don't start polling
                if (initialStatus.status === 'completed' || initialStatus.status === 'failed') {
                    setLoadingReconciliation(false);
                    
                    // Show final results
                    if ((!initialStatus.to_reconcile || initialStatus.to_reconcile.length === 0) && initialStatus.failed_count === 0) {
                        setReconciliationMessage({
                            type: 'success',
                            text: t('admin.systemSettings.reconciliation.noDiscrepancies', 
                                'No discrepancies found. All subscriptions are in sync!')
                        });
                    } else {
                        setReconciliationMessage({
                            type: 'info',
                            text: t('admin.systemSettings.reconciliation.foundMissing', 
                                'Found {{count}} subscriptions that need reconciliation.', 
                                { count: initialStatus.to_reconcile?.length || 0 })
                        });
                    }
                    return;
                }
            } catch (error) {
                console.error('Error checking initial job status:', error);
            }
            
            // Then set up interval polling
            const newIntervalId = window.setInterval(async () => {
                // Use the job ID from closure to ensure we're using the correct one
                const currentJobId = jobResponse.job_id;
                
                if (!currentJobId || !user?.token) {
                    clearInterval(newIntervalId);
                    intervalIdRef.current = null;
                    setLoadingReconciliation(false);
                    return;
                }
                
                try {
                    const jobStatus = await SubscriptionService.checkReconciliationJobStatus(user.token, currentJobId);
                    
                    // Update progress
                    if (jobStatus.pagination) {
                        setReconciliationProgress({
                            current: jobStatus.pagination.processed_count,
                            total: jobStatus.pagination.total_count
                        });
                    }
                    
                    // Update data
                    setReconciliationData(jobStatus);
                    
                    // Update message based on job status
                    if (jobStatus.status === 'running') {
                        setReconciliationMessage({
                            type: 'info',
                            text: t('admin.systemSettings.reconciliation.jobRunning', 
                                'Processing {{processed}} of {{total}} users...', 
                                { 
                                    processed: jobStatus.pagination?.processed_count || 0,
                                    total: jobStatus.pagination?.total_count || 0 
                                })
                        });
                    } else if (jobStatus.status === 'completed') {
                        // Job is complete, stop polling
                        clearInterval(newIntervalId);
                        intervalIdRef.current = null;
                        setLoadingReconciliation(false);
                        
                        // Show final results
                        if ((!jobStatus.to_reconcile || jobStatus.to_reconcile.length === 0) && (jobStatus.failed_count === 0 || !jobStatus.failed_count)) {
                            setReconciliationMessage({
                                type: 'success',
                                text: t('admin.systemSettings.reconciliation.noDiscrepancies', 
                                    'No discrepancies found. All subscriptions are in sync!')
                            });
                        } else {
                            setReconciliationMessage({
                                type: 'info',
                                text: t('admin.systemSettings.reconciliation.foundMissing', 
                                    'Found {{count}} subscriptions that need reconciliation.', 
                                    { count: jobStatus.to_reconcile?.length || 0 })
                            });
                        }
                    } else if (jobStatus.status === 'failed') {
                        // Job failed, stop polling
                        clearInterval(newIntervalId);
                        intervalIdRef.current = null;
                        setLoadingReconciliation(false);
                        
                        // Show error message
                        setReconciliationMessage({
                            type: 'error',
                            text: t('admin.systemSettings.reconciliation.jobFailed', 
                                'Reconciliation job failed: {{error}}', 
                                { error: jobStatus.error || 'Unknown error' })
                        });
                    }
                } catch (error: any) {
                    console.error('Error checking job status:', error);
                    
                    // Check if this is a 409 Conflict error (another job is running)
                    if (error.response && error.response.status === 409) {
                        // Another job is running - try to get its status instead
                        try {
                            // Get the running job ID from the response if available
                            const runningJobId = error.response.data?.running_job_id;
                            
                            if (runningJobId) {
                                // Update our job ID to track the running job instead
                                setReconciliationJobId(runningJobId);
                                console.log(`Switching to track existing job: ${runningJobId}`);
                                
                                // Don't stop polling - we'll check the existing job on the next interval
                                setReconciliationMessage({
                                    type: 'info',
                                    text: t('admin.systemSettings.reconciliation.existingJob', 
                                        'Another reconciliation job is already running. Showing its status instead.')
                                });
                                return;
                            }
                        } catch (innerError) {
                            console.error('Error handling conflict response:', innerError);
                        }
                    }
                    
                    // For other errors, stop polling and show error message
                    clearInterval(newIntervalId);
                    intervalIdRef.current = null;
                    setLoadingReconciliation(false);
                    
                    setReconciliationMessage({
                        type: 'error',
                        text: t('admin.systemSettings.reconciliation.jobError', 
                            'Error checking reconciliation status. Please try again.')
                    });
                }
            }, 5000); // Check every 5 seconds
            
            intervalIdRef.current = newIntervalId;
            
        } catch (error) {
            console.error('Error starting reconciliation job:', error);
            setReconciliationMessage({
                type: 'error',
                text: t('admin.systemSettings.reconciliation.startError', 'Error starting reconciliation job. Please try again.')
            });
            setLoadingReconciliation(false);
        }
    };
    
    const reconcileItem = async (item: ReconciliationItem) => {
        if (!user?.token) return;
        
        // Set processing state for this item
        setProcessingItems((prev: Record<string, boolean>) => ({
            ...prev,
            [item.stripe_subscription_id]: true
        }));
        
        try {
            const response = await SubscriptionService.reconcileSubscription({ stripe_subscription_id: item.stripe_subscription_id }, user.token);
            
            // Get the subscription ID from the response if available
            let subscriptionId: number | undefined = undefined;
            if (response && response.reconciled && response.reconciled.length > 0) {
                subscriptionId = response.reconciled[0].subscription_id;
            }
            
            // Update the reconciliation data to remove this item
            setReconciliationData(prev => {
                if (!prev) return null;
                
                // Remove the item from to_reconcile list
                const updatedToReconcile = prev.to_reconcile?.filter(
                    i => i.stripe_subscription_id !== item.stripe_subscription_id
                ) || [];
                
                // Also remove from selectedItems if it was selected
                if (selectedItems.includes(item.stripe_subscription_id)) {
                    setSelectedItems((prev: string[]) => prev.filter((id: string) => id !== item.stripe_subscription_id));
                }
                
                // Create a new reconciled item with the subscription ID
                const reconciledItem: ReconciliationItem = {
                    ...item,
                    subscription_id: subscriptionId
                };
                
                return {
                    ...prev,
                    reconciled_count: prev.reconciled_count + 1,
                    reconciled: [...(prev.reconciled || []), reconciledItem],
                    to_reconcile: updatedToReconcile
                };
            });
            
            // Show success message
            setReconciliationMessage({
                type: 'success',
                text: t('admin.systemSettings.reconciliation.itemSuccess', 
                    'Successfully reconciled subscription for {{email}}', 
                    { email: item.email })
            });
        } catch (error) {
            console.error('Error reconciling item:', error);
            setReconciliationMessage({
                type: 'error',
                text: t('admin.systemSettings.reconciliation.itemError', 
                    'Error reconciling subscription for {{email}}. Please try again.', 
                    { email: item.email })
            });
        } finally {
            // Clear processing state
            setProcessingItems((prev: Record<string, boolean>) => {
                const newState = { ...prev };
                delete newState[item.stripe_subscription_id];
                return newState;
            });
        }
    };
    
    const reconcileSelected = async () => {
        if (!user?.token || selectedItems.length === 0) return;
        
        setLoadingReconciliation(true);
        setReconciliationMessage({
            type: 'info',
            text: t('admin.systemSettings.reconciliation.processingSelected', 'Processing {{count}} selected subscriptions...', 
                { count: selectedItems.length })
        });
        
        try {
            // Process each selected item one by one
            let successCount = 0;
            let failedCount = 0;
            
            // Get items to reconcile based on selected IDs
            const itemsToReconcile = reconciliationData?.to_reconcile?.filter(
                item => selectedItems.includes(item.stripe_subscription_id)
            ) || [];
            
            // Create a copy of the to_reconcile list that we'll update as we go
            let updatedToReconcile = [...(reconciliationData?.to_reconcile || [])];
            
            for (const item of itemsToReconcile) {
                try {
                    // Mark this item as processing
                    setProcessingItems((prev: Record<string, boolean>) => ({
                        ...prev,
                        [item.stripe_subscription_id]: true
                    }));
                    
                    await SubscriptionService.reconcileSubscription({ stripe_subscription_id: item.stripe_subscription_id }, user.token);
                    
                    // Remove this item from the to_reconcile list
                    updatedToReconcile = updatedToReconcile.filter(
                        i => i.stripe_subscription_id !== item.stripe_subscription_id
                    );
                    
                    // Update the reconciliation data
                    setReconciliationData(prev => {
                        if (!prev) return null;
                        
                        return {
                            ...prev,
                            reconciled_count: prev.reconciled_count + 1,
                            reconciled: [...(prev.reconciled || []), item],
                            to_reconcile: updatedToReconcile
                        };
                    });
                    
                    successCount++;
                } catch (err) {
                    console.error(`Error reconciling subscription ${item.stripe_subscription_id}:`, err);
                    failedCount++;
                } finally {
                    // Clear processing state for this item
                    setProcessingItems((prev: Record<string, boolean>) => {
                        const newState = { ...prev };
                        delete newState[item.stripe_subscription_id];
                        return newState;
                    });
                }
            }
            
            // Clear selection
            setSelectedItems([]);
            
            setReconciliationMessage({
                type: 'success',
                text: t('admin.systemSettings.reconciliation.selectedSuccess', 
                    'Successfully reconciled {{success}} subscriptions. {{failed}} failed.', 
                    { success: successCount, failed: failedCount })
            });
        } catch (error) {
            console.error('Error reconciling selected items:', error);
            setReconciliationMessage({
                type: 'error',
                text: t('admin.systemSettings.reconciliation.allError', 'Error reconciling subscriptions. Please try again.')
            });
        } finally {
            setLoadingReconciliation(false);
        }
    };
    
    // The reconcileAll function has been removed as it's no longer needed
    
    const handleSelectItem = (subId: string) => {
        setSelectedItems((prev: string[]) => {
            if (prev.includes(subId)) {
                return prev.filter((id: string) => id !== subId);
            } else {
                return [...prev, subId];
            }
        });
    };
    
    const handleSelectAll = () => {
        if (!reconciliationData?.to_reconcile) return;
        
        if (selectedItems.length === reconciliationData.to_reconcile.length) {
            // If all are selected, deselect all
            setSelectedItems([]);
        } else {
            // Otherwise select all
            setSelectedItems(reconciliationData.to_reconcile.map(item => item.stripe_subscription_id));
        }
    };

    const hasChanges = 
        maxFreeCertificates !== originalValue || 
        emailCertificateEnabled !== originalEmailEnabled ||
        allowNameFieldsEditing !== originalAllowNameFieldsEditing ||
        batchReportTime !== originalBatchReportTime ||
        JSON.stringify(batchReportEmails.sort()) !== JSON.stringify(originalBatchReportEmails.sort());

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                {t('admin.systemSettings.title', 'System Settings')}
            </Typography>

            {message && (
                <Alert 
                    severity={message.type}
                    sx={{ 
                        mb: 3,
                        '& .MuiAlert-message': {
                            fontSize: '1rem'
                        }
                    }}
                >
                    {message.text}
                </Alert>
            )}

            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {t('admin.systemSettings.generalSettings', 'General Settings')}
                </Typography>
                <Box sx={{ maxWidth: 600 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 120px',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <Typography sx={{ pr: 15, whiteSpace: 'nowrap' }}>
                                {t('admin.systemSettings.maxCertificates', 'Maximum Free Certificates')}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '120px' }}>
                                <TextField
                                    type="number"
                                    value={maxFreeCertificates}
                                    onChange={(e) => {
                                        setMaxFreeCertificates(e.target.value);
                                        setMessage(null);
                                    }}
                                    disabled={saving}
                                    size="small"
                                    sx={{ width: '80px' }}
                                    inputProps={{ style: { textAlign: 'center' } }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 120px',
                            alignItems: 'center',
                            gap: 2,
                            mb: 0 // No bottom margin
                        }}>
                            <Typography sx={{ whiteSpace: 'nowrap' }}>
                                {t('admin.systemSettings.emailCertificates', 'Email Certificates')}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '120px' }}>
                                <IconButton
                                    onClick={() => {
                                        setEmailCertificateEnabled(!emailCertificateEnabled);
                                        setMessage(null);
                                    }}
                                    disabled={saving}
                                    color={emailCertificateEnabled ? "success" : "error"}
                                    size="large"
                                    sx={{
                                        p: 0, // No padding
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '3rem'
                                        }
                                    }}
                                >
                                    {emailCertificateEnabled ? <EnableIcon /> : <DisableIcon />}
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 120px',
                            alignItems: 'center',
                            gap: 2,
                            mt: -3 // Significant negative top margin to pull up
                        }}>
                            <Typography sx={{ whiteSpace: 'nowrap' }}>
                                {t('admin.systemSettings.allowNameFieldsEditing', 'Allow Name Fields Editing')}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '120px' }}>
                                <IconButton
                                    onClick={() => {
                                        setAllowNameFieldsEditing(!allowNameFieldsEditing);
                                        setMessage(null);
                                    }}
                                    disabled={saving}
                                    color={allowNameFieldsEditing ? "success" : "error"}
                                    size="large"
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '3rem'
                                        }
                                    }}
                                >
                                    {allowNameFieldsEditing ? <EnableIcon /> : <DisableIcon />}
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 3 }} />
                        
                        <Typography variant="h6" gutterBottom>
                            {t('admin.systemSettings.batchReportSettings', 'Batch Report Settings')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('admin.systemSettings.batchReportDescription', 'Configure the automated unverified students report that will be sent daily.')}
                        </Typography>
                        
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 160px',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <Typography sx={{ pr: 15, whiteSpace: 'nowrap' }}>
                                {t('admin.systemSettings.batchReportTime', 'Batch Report Time')}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '160px' }}>
                                <TextField
                                    type="time"
                                    value={batchReportTime}
                                    onChange={(e) => {
                                        setBatchReportTime(e.target.value);
                                        setMessage(null);
                                    }}
                                    disabled={saving}
                                    size="small"
                                    sx={{ width: '160px' }}
                                    inputProps={{ style: { textAlign: 'center' } }}
                                />
                            </Box>
                        </Box>
                        
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ mb: 1 }}>
                                {t('admin.systemSettings.batchReportEmails', 'Batch Report Recipient Email List')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={t('admin.systemSettings.addEmailPlaceholder', 'Add email address')}
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddEmail();
                                        }
                                    }}
                                    disabled={saving}
                                />
                                <Button 
                                    variant="outlined" 
                                    onClick={handleAddEmail}
                                    disabled={saving || !newEmail}
                                >
                                    {t('admin.systemSettings.addEmail', 'Add')}
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {batchReportEmails.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('admin.systemSettings.noEmailsAdded', 'No recipient emails added yet')}
                                    </Typography>
                                ) : (
                                    batchReportEmails.map((email) => (
                                        <Chip
                                            key={email}
                                            label={email}
                                            onDelete={() => handleRemoveEmail(email)}
                                            disabled={saving}
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>
                        
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving || !hasChanges}
                                color="primary"
                            >
                                {saving ? (
                                    <>
                                        <CircularProgress size={24} sx={{ mr: 1 }} />
                                        {t('admin.systemSettings.saving', 'Saving...')}
                                    </>
                                ) : (
                                    t('admin.systemSettings.saveChanges', 'Save Changes')
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Accordion defaultExpanded sx={{ mt: 3 }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="subscription-reconciliation-content"
                    id="subscription-reconciliation-header"
                >
                    <Typography variant="h6">
                        {t('admin.systemSettings.reconciliation.title', 'Subscription Reconciliation')}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body1" paragraph>
                        {t('admin.systemSettings.reconciliation.description', 
                            'Check for discrepancies between Stripe subscriptions and database records. This tool helps identify users who have paid in Stripe but don\'t have an active subscription in the database.')}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Button
                            variant="contained"
                            onClick={checkReconciliation}
                            disabled={loadingReconciliation}
                            startIcon={loadingReconciliation ? <CircularProgress size={20} /> : <RefreshIcon />}
                        >
                            {loadingReconciliation
                                ? t('admin.systemSettings.reconciliation.checking', 'Checking...')
                                : t('admin.systemSettings.reconciliation.check', 'Check for Discrepancies')}
                        </Button>
                    </Box>
                    
                    {reconciliationMessage && (
                        <Alert 
                            severity={reconciliationMessage.type}
                            sx={{ mb: 3 }}
                        >
                            {reconciliationMessage.text}
                        </Alert>
                    )}
                    
                    {reconciliationData && (
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Discrepancies found */}
                            {reconciliationData.to_reconcile && reconciliationData.to_reconcile.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {t('admin.systemSettings.reconciliation.discrepanciesTitle', 
                                                'Discrepancies Found ({{count}})', 
                                                { count: reconciliationData.to_reconcile.length })}
                                        </Typography>
                                        
                                        {selectedItems.length > 0 && (
                                            <Button 
                                                variant="contained" 
                                                color="primary" 
                                                size="small"
                                                startIcon={<SyncIcon />}
                                                onClick={reconcileSelected}
                                                disabled={loadingReconciliation}
                                            >
                                                {t('admin.systemSettings.reconciliation.bulkReconcile', 
                                                    'Bulk Reconcile Selected ({{count}})', 
                                                    { count: selectedItems.length })}
                                            </Button>
                                        )}
                                    </Box>
                                    
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            indeterminate={selectedItems.length > 0 && selectedItems.length < reconciliationData.to_reconcile.length}
                                                            checked={selectedItems.length === reconciliationData.to_reconcile.length && reconciliationData.to_reconcile.length > 0}
                                                            onChange={handleSelectAll}
                                                            disabled={loadingReconciliation}
                                                        />
                                                    </TableCell>
                                                    <TableCell width="15%">{t('admin.systemSettings.reconciliation.user', 'User')}</TableCell>
                                                    <TableCell width="13%">{t('admin.systemSettings.reconciliation.stripeSubscriptionId', 'Stripe Subscription ID')}</TableCell>
                                                    <TableCell width="13%" align="center">{t('admin.systemSettings.reconciliation.stripeDate', 'Stripe Date')}</TableCell>
                                                    <TableCell width="13%" align="center">{t('admin.systemSettings.reconciliation.dbDate', 'Database Date')}</TableCell>
                                                    <TableCell width="34%">{t('admin.systemSettings.reconciliation.reason', 'Reason')}</TableCell>
                                                    <TableCell width="12%" align="center">{t('admin.systemSettings.reconciliation.action', 'Action')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {reconciliationData.to_reconcile.map((item) => (
                                                    <TableRow key={item.stripe_subscription_id}>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedItems.includes(item.stripe_subscription_id)}
                                                                onChange={() => handleSelectItem(item.stripe_subscription_id)}
                                                                disabled={loadingReconciliation || processingItems[item.stripe_subscription_id]}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{item.email}</TableCell>
                                                        <TableCell>
                                                            <Tooltip title={item.stripe_subscription_id}>
                                                                <span>{item.stripe_subscription_id.substring(0, 10)}...</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell align="center">{formatDateTwoLines(item.stripe_created_date)}</TableCell>
                                                        <TableCell align="center">{formatDateTwoLines(item.db_subscription_date)}</TableCell>
                                                        <TableCell>{item.reason}</TableCell>
                                                        <TableCell align="center">
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => reconcileItem(item)}
                                                                disabled={loadingReconciliation || processingItems[item.stripe_subscription_id]}
                                                                sx={{ minWidth: 'unset', px: 1 }}
                                                            >
                                                                {processingItems[item.stripe_subscription_id] ? (
                                                                    <CircularProgress size={20} color="inherit" />
                                                                ) : (
                                                                    t('admin.systemSettings.reconciliation.reconcile', 'Reconcile')
                                                                )}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                            
                            {/* Reconciled subscriptions */}
                            {reconciliationData.reconciled && reconciliationData.reconciled.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}>
                                        {t('admin.systemSettings.reconciliation.reconciledTitle', 
                                            'Reconciled Subscriptions ({{count}})', 
                                            { count: reconciliationData.reconciled.length })}
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t('admin.systemSettings.reconciliation.email', 'User Email')}</TableCell>
                                                    <TableCell>{t('admin.systemSettings.reconciliation.stripeId', 'Stripe Subscription ID')}</TableCell>
                                                    <TableCell>{t('admin.systemSettings.reconciliation.dbId', 'Database Subscription ID')}</TableCell>
                                                    <TableCell align="right">{t('admin.systemSettings.reconciliation.status', 'Status')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {reconciliationData.reconciled.map((item) => (
                                                    <TableRow key={item.stripe_subscription_id}>
                                                        <TableCell>{item.email}</TableCell>
                                                        <TableCell>
                                                            <Tooltip title={item.stripe_subscription_id}>
                                                                <span>{item.stripe_subscription_id.substring(0, 10)}...</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>{item.subscription_id}</TableCell>
                                                        <TableCell align="right">
                                                            <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                                <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                                                                {t('admin.systemSettings.reconciliation.reconciled', 'Reconciled')}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                            
                            {/* Failed reconciliations */}
                            {reconciliationData.failed && reconciliationData.failed.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'error.main' }}>
                                        {t('admin.systemSettings.reconciliation.failedTitle', 
                                            'Failed Reconciliations ({{count}})', 
                                            { count: reconciliationData.failed.length })}
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t('admin.systemSettings.reconciliation.email', 'User Email')}</TableCell>
                                                    <TableCell>{t('admin.systemSettings.reconciliation.stripeId', 'Stripe Subscription ID')}</TableCell>
                                                    <TableCell>{t('admin.systemSettings.reconciliation.reason', 'Reason')}</TableCell>
                                                    <TableCell align="right">{t('admin.systemSettings.reconciliation.status', 'Status')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {reconciliationData.failed.map((item) => (
                                                    <TableRow key={item.stripe_subscription_id}>
                                                        <TableCell>{item.email}</TableCell>
                                                        <TableCell>
                                                            <Tooltip title={item.stripe_subscription_id}>
                                                                <span>{item.stripe_subscription_id.substring(0, 10)}...</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>{item.reason}</TableCell>
                                                        <TableCell align="right">
                                                            <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                                <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                                                                {t('admin.systemSettings.reconciliation.failed', 'Failed')}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        </Container>
    );
};

export default SystemSettings;
